import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Sprint polish: announce route changes to assistive tech and move
 * keyboard focus to the page heading so keyboard-only users do not
 * lose their place after a navigation.
 *
 * How it works:
 * - The router's `pathname` is the natural key for "where are we".
 * - We look for the first `<h1>` inside the new page on the next
 *   animation frame (after React has committed the new tree).
 * - If we find one, we make it focusable (`tabindex = -1`) and
 *   move focus to it. Otherwise we focus the main content area.
 * - A polite live region announces the new page title so screen
 *   reader users hear the change even if they were not focused on
 *   the page.
 *
 * Mounted once near the root of the tree, alongside `<App />`.
 */
export function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;

    const id = window.requestAnimationFrame(() => {
      const main = document.querySelector<HTMLElement>("main, [role='main'], .ds-page");
      const heading = main?.querySelector<HTMLHeadingElement>("h1");
      const target = heading ?? main;
      if (target && !target.hasAttribute("tabindex")) {
        target.tabIndex = -1;
      }
      if (target) {
        target.focus({ preventScroll: false });
      }
      const title =
        heading?.textContent?.trim() ||
        main?.getAttribute("aria-label") ||
        document.title ||
        "Янги саҳифа";
      setAnnouncement(title);
    });

    return () => window.cancelAnimationFrame(id);
  }, [location.pathname]);

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: "absolute",
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
        border: 0,
      }}
    >
      {announcement}
    </div>
  );
}
